import React, { useState, useEffect } from 'react';
import { X, Send, Plus, Trash2, Check } from 'lucide-react';
import apiService from './apiService';

const DoctorRoutingModal = ({ patient, diagnosis, onComplete, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [treatmentPlan, setTreatmentPlan] = useState('');
    const [selectedSteps, setSelectedSteps] = useState([]);
    const [availableRooms, setAvailableRooms] = useState({});

    const stepTypes = [
        { type: 'lab', label: 'Laboratory', icon: '🔬', requiresReturn: true },
        { type: 'imaging', label: 'Imaging (X-ray/CT)', icon: '📷', requiresReturn: true },
        { type: 'pharmacy', label: 'Pharmacy', icon: '💊', requiresReturn: false },
        { type: 'procedure', label: 'Procedure Room', icon: '🏥', requiresReturn: false },
        { type: 'surgery', label: 'Surgery', icon: '⚕️', requiresReturn: false },
        { type: 'ward', label: 'Hospital Admission', icon: '🛏️', requiresReturn: false },
        { type: 'billing', label: 'Billing', icon: '💰', requiresReturn: false }
    ];

    useEffect(() => {
        // Pre-fetch rooms for common types
        fetchRoomsForType('lab');
        fetchRoomsForType('imaging');
        fetchRoomsForType('pharmacy');
        fetchRoomsForType('billing');
    }, []);

    const fetchRoomsForType = async (type) => {
        try {
            const rooms = await apiService.getAvailableRooms(type);
            setAvailableRooms(prev => ({ ...prev, [type]: rooms }));
        } catch (error) {
            console.error(`Error fetching ${type} rooms:`, error);
        }
    };

    const toggleStep = (stepType) => {
        const exists = selectedSteps.find(s => s.locationType === stepType.type);
        if (exists) {
            setSelectedSteps(selectedSteps.filter(s => s.locationType !== stepType.type));
        } else {
            const rooms = availableRooms[stepType.type] || [];
            const newStep = {
                locationType: stepType.type,
                locationId: rooms.length > 0 ? rooms[0].id : null,
                roomName: rooms.length > 0 ? rooms[0].name : null,
                requiresReturn: stepType.requiresReturn,
                priority: 'routine',
                testType: '',
                instructions: '',
                estimatedDuration: 15
            };
            setSelectedSteps([...selectedSteps, newStep]);
        }
    };

    const updateStep = (index, field, value) => {
        const updated = [...selectedSteps];
        updated[index] = { ...updated[index], [field]: value };
        
        // If location changed, update room name
        if (field === 'locationId') {
            const rooms = availableRooms[updated[index].locationType] || [];
            const room = rooms.find(r => r.id === value);
            if (room) {
                updated[index].roomName = room.name;
            }
        }
        
        setSelectedSteps(updated);
    };

    const handleSubmit = async () => {
        if (selectedSteps.length === 0) {
            alert('Please select at least one next step for the patient');
            return;
        }

        setLoading(true);
        try {
            const result = await apiService.completeConsultationAndRoute({
                patientId: patient.id,
                diagnosis,
                treatmentPlan,
                nextSteps: selectedSteps
            });
            
            onComplete(result);
        } catch (error) {
            console.error('Error creating patient journey:', error);
            alert(`Failed to create patient journey: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Complete Consultation & Create Patient Journey</h3>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Patient Info */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="font-semibold text-gray-900">{patient.name}</p>
                        <p className="text-sm text-gray-600">Queue #{String(patient.queueNumber).padStart(3, '0')} • {patient.department}</p>
                        {diagnosis && <p className="text-sm text-gray-700 mt-2"><span className="font-medium">Diagnosis:</span> {diagnosis}</p>}
                    </div>

                    {/* Treatment Plan */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Treatment Plan Summary (Optional)
                        </label>
                        <textarea
                            value={treatmentPlan}
                            onChange={(e) => setTreatmentPlan(e.target.value)}
                            rows={2}
                            placeholder="Brief treatment plan summary..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Step Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Patient Needs (Select all that apply):
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            {stepTypes.map((stepType) => {
                                const isSelected = selectedSteps.some(s => s.locationType === stepType.type);
                                return (
                                    <button
                                        key={stepType.type}
                                        type="button"
                                        onClick={() => toggleStep(stepType)}
                                        className={`p-3 border-2 rounded-lg text-left transition-all ${
                                            isSelected
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-blue-300'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-2xl">{stepType.icon}</span>
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-900 text-sm">{stepType.label}</p>
                                                {stepType.requiresReturn && (
                                                    <p className="text-xs text-gray-500">Returns to you</p>
                                                )}
                                            </div>
                                            {isSelected && <Check className="w-5 h-5 text-blue-600" />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Step Details */}
                    {selectedSteps.length > 0 && (
                        <div className="border-t pt-4">
                            <h4 className="font-semibold text-gray-900 mb-3">Step Details:</h4>
                            <div className="space-y-4">
                                {selectedSteps.map((step, index) => {
                                    const rooms = availableRooms[step.locationType] || [];
                                    const stepLabel = stepTypes.find(t => t.type === step.locationType)?.label;
                                    
                                    return (
                                        <div key={index} className="p-4 bg-gray-50 rounded-lg border">
                                            <div className="flex items-center justify-between mb-3">
                                                <p className="font-medium text-gray-900">
                                                    Step {index + 1}: {stepLabel}
                                                </p>
                                                <button
                                                    onClick={() => setSelectedSteps(selectedSteps.filter((_, i) => i !== index))}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            
                                            {/* Room Selection */}
                                            <div className="mb-3">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Select Room:</label>
                                                <select
                                                    value={step.locationId || ''}
                                                    onChange={(e) => updateStep(index, 'locationId', parseInt(e.target.value))}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                                >
                                                    {rooms.map(room => (
                                                        <option key={room.id} value={room.id}>
                                                            {room.name} {room.fullAddress ? `- ${room.fullAddress}` : ''}
                                                        </option>
                                                    ))}
                                                    {rooms.length === 0 && (
                                                        <option value="">No rooms available</option>
                                                    )}
                                                </select>
                                            </div>

                                            {/* Test Type (for lab/imaging) */}
                                            {['lab', 'imaging'].includes(step.locationType) && (
                                                <div className="mb-3">
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        {step.locationType === 'lab' ? 'Tests Ordered:' : 'Imaging Type:'}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={step.testType}
                                                        onChange={(e) => updateStep(index, 'testType', e.target.value)}
                                                        placeholder={step.locationType === 'lab' ? 'e.g., CBC, Lipid Panel' : 'e.g., Chest X-ray PA'}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                            )}

                                            {/* Priority */}
                                            <div className="mb-3">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Priority:</label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => updateStep(index, 'priority', 'routine')}
                                                        className={`px-2 py-1 rounded text-xs font-medium ${
                                                            step.priority === 'routine'
                                                                ? 'bg-green-500 text-white'
                                                                : 'bg-gray-100 text-gray-700'
                                                        }`}
                                                    >
                                                        Routine
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => updateStep(index, 'priority', 'urgent')}
                                                        className={`px-2 py-1 rounded text-xs font-medium ${
                                                            step.priority === 'urgent'
                                                                ? 'bg-red-500 text-white'
                                                                : 'bg-gray-100 text-gray-700'
                                                        }`}
                                                    >
                                                        Urgent
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Instructions */}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Patient Instructions:</label>
                                                <input
                                                    type="text"
                                                    value={step.instructions}
                                                    onChange={(e) => updateStep(index, 'instructions', e.target.value)}
                                                    placeholder="e.g., Fasting required for blood test"
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Journey Preview */}
                    {selectedSteps.length > 0 && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <p className="font-semibold text-gray-900 mb-2">Patient Journey Preview:</p>
                            <div className="space-y-2">
                                {selectedSteps.map((step, index) => {
                                    const stepLabel = stepTypes.find(t => t.type === step.locationType)?.label;
                                    return (
                                        <div key={index} className="flex items-start gap-2 text-sm">
                                            <span className="font-bold text-blue-600">{index + 1}.</span>
                                            <div>
                                                <span className="font-medium">{stepLabel}</span>
                                                {step.roomName && <span className="text-gray-600"> - {step.roomName}</span>}
                                                {step.requiresReturn && (
                                                    <span className="text-green-600 text-xs ml-2">→ Returns to you</span>
                                                )}
                                                {step.testType && (
                                                    <p className="text-xs text-gray-500">Tests: {step.testType}</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-gray-600 mt-3">
                                ℹ️ Patient will receive a printed pathway slip with all steps
                            </p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4 border-t">
                        <button
                            onClick={onCancel}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading || selectedSteps.length === 0}
                            className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                        >
                            {loading ? 'Creating Journey...' : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Complete & Create Journey
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DoctorRoutingModal;

